/* eslint-disable react/destructuring-assignment */
import React, { useReducer, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import WarningYellowOutline from "@druva-apps/orion-icons/lib/WarningYellowOutline";
import MessageBox from "../../messageBox";

import { Provider } from "../context";
import {
  useColumnsFromColumnsDef,
  useRowSelect,
  useUpdateContextOnPropChanges
} from "../hooks";
import {
  storeTableConfigInLocalStorage,
  hydradeColumnsFromLocalStorage
} from "../utils";
import { updateStateValues } from "../actions";
import tableReducer from "../reducer";
import Table from "./Table";
import "./style.scss";

const TableProvider = ({
  children,
  defaultSelectedRows,
  defaultExpandedRows,
  expandedRows,
  columnsDef,
  ...props
}) => {
  const { columns, maxHeaderCount } = useColumnsFromColumnsDef(
    columnsDef,
    props.responsive
  );
  const exportableColumnKeys = columns
    .filter(column => typeof column.exportable === "undefined")
    .map(column => column.key);
  const [state, dispatch] = useReducer(tableReducer, {
    ...props,
    columns: hydradeColumnsFromLocalStorage(
      columns,
      props.tableId,
      props.enablePersistConfig
    ),
    exportableColumnKeys,
    maxHeaderCount,
    isInitialWidthSet: false,
    tableWidth: 0,
    sortBy: null,
    sortOrder: null,
    allRowsSelected: false,
    selectedRow: null,
    selectedRows: defaultSelectedRows || [],
    controlSelection: !!props.selectedRows,
    expandedRows: expandedRows || defaultExpandedRows || [],
    controlExpansion: !!expandedRows,
    isLoadingMore: props.isLoadingMore
  });

  const fireAction = useCallback(action => dispatch(action), []);
  const value = { ...state, fireAction };

  useRowSelect(value, props);
  useUpdateContextOnPropChanges(fireAction, props, value);

  const deriveColumnsFromProps = useCallback((newColumns, oldColumns) => {
    return oldColumns.map(column => {
      const { key } = column;
      const respectiveNewColumn = newColumns.find(c => c.key === key);

      if (respectiveNewColumn) {
        const { width, hidden, frozen } = column;
        return {
          ...respectiveNewColumn,
          width,
          hidden,
          frozen,
          timestamp: Date.now() // To fix https://github.com/Autodesk/react-base-table/issues/111
        };
      }

      return column;
    });
  }, []);

  useEffect(() => {
    fireAction(
      updateStateValues({
        columns: deriveColumnsFromProps(columns, value.columns)
      })
    );
  }, [fireAction, columns]);

  useEffect(() => {
    storeTableConfigInLocalStorage(
      props.tableId,
      props.enablePersistConfig,
      value.columns
    );
  }, [value.columns, props.tableId, props.enablePersistConfig]);

  return <Provider value={value}>{children || <Table />}</Provider>;
};

TableProvider.propTypes = {
  /**
   * Unique Table ID (Very useful in case of multiple tables on same page).
   */
  tableId: PropTypes.string,

  /**
   * Children of the table provider which will be exported compound components.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),

  /**
   * Array of column objects.
   */
  columnsDef: PropTypes.array.isRequired,

  /**
   * Array of data items for each row. They should contain unique id.
   */
  data: PropTypes.array.isRequired,

  /**
   * This key will be used to identify rows in a dataset. Default is `id`.
   */
  uniqueDataKey: PropTypes.string,

  /**
   * Width of the table. Ideally it shouldn't be provided as we're using AutoSizer.
   */
  width: PropTypes.number,

  /**
   * Height of the table in pixels.
   */
  height: PropTypes.number,

  /**
   * Whether the table should be responsive. If true, all columns will be visible.
   */
  responsive: PropTypes.bool,

  /**
   * A boolean flag to hide the table headers.
   */
  hideHeader: PropTypes.bool,

  /**
   * Whether we want the checkbox/radio column to be sticky to left side.
   */
  fixSelectionColumn: PropTypes.bool,

  /**
   * A boolean flag to indicate the table rows might be expandable
   */
  isExpandable: PropTypes.bool,

  /**
   * A boolean flag to indicate the table is selectable with checkbox/radio.
   */
  isSelectable: PropTypes.bool,

  /**
   * A boolean flag to indicate multiple rows can be selected in the table.
   */
  multiSelect: PropTypes.bool,

  /**
   * An array of unique IDs whose rows will be disabled to be checked.
   */
  disabledRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),

  /**
   * An array of initially checked row IDs.
   */
  defaultSelectedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),

  /**
   * An array of selected row IDs. Only provide if selection is maintained outside of the table.
   */
  selectedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),

  /**
   * An array of initially expanded row IDs.
   */
  defaultExpandedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),

  /**
   * An array of expanded row IDs.
   */
  expandedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),

  /**
   * A boolean flag to render loading overlay over the entire table.
   */
  isLoading: PropTypes.bool,

  /**
   * A boolean flag to render load more loader on bottom of the table.
   */
  isLoadingMore: PropTypes.bool,

  /**
   * A text string to be rendered before load more spinner.
   */
  loadingMoreText: PropTypes.string,

  /**
   * A boolean flag to tell all records have been loaded. So scroll down will not be triggered.
   */
  loadedAll: PropTypes.bool,

  /**
   * If footerRenderer is provided, this is required. Default is 40.
   */
  footerHeight: PropTypes.number,

  /**
   * A callback handler to be called on click of select all checkbox.
   */
  onSelectAll: PropTypes.func,

  /**
   * An estimated height of each row to prevent flickering as much as possible
   */
  estimatedRowHeight: PropTypes.number,

  /**
   * A callback handler to be called on click of any row.
   */
  onRowClick: PropTypes.func,

  /**
   * A function to be called while rendering the checkbox/radio to get the tooltip for it.
   */
  getSelectionInputTooltip: PropTypes.func,

  /**
   * A function to be called while rendering rows to get their class names from the consumer.
   */
  getRowClassName: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

  /**
   * A callback to be called when the table selection is changed (checked/unchecked any row).
   */
  onRowSelectionChange: PropTypes.func,

  /**
   * A callback to be called when the scroll reaches to the bottom of the table.
   */
  onLoadMore: PropTypes.func,

  /**
   * A callback to be called when click on row expand icon.
   */
  onRowExpand: PropTypes.func,

  /**
   * A custom row renderer which is useful when we want to render some details of a parent row.
   */
  rowRenderer: PropTypes.func,

  /**
   * A callback to be called when there is no data available to the table.
   */
  emptyRenderer: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

  /**
   * A function to be called when footer is required to be rendered at below the table.
   */
  footerRenderer: PropTypes.func,

  /**
   * Enable/Disable table configuration button
   */
  enableConfiguration: PropTypes.bool,

  /**
   * Enable/Disable full screen feature
   */
  enableFullScreen: PropTypes.bool,

  /**
   * Enable/Disable export to csv feature
   */
  enableExportToCsv: PropTypes.bool,

  /**
   * CSV File name if enableExportToCsv is true
   */
  csvFileName: PropTypes.string,

  /**
   * Column name for data is default sorted by
   */
  sortBy: PropTypes.string,

  /**
   * Default sorting order of column specified in sortBy
   */
  sortOrder: PropTypes.oneOf(["asc", "desc"]),

  /**
   * On column sort handler
   */
  onSort: PropTypes.func,

  /**
   * Globally control resizable
   */
  resizable: PropTypes.bool,

  /**
   * Max height for export to csv popup columns container
   */
  exportPopupMaxHeight: PropTypes.number,

  /**
   * Enable/Disable row split feature
   */
  enableRowSpan: PropTypes.bool,

  /**
   * Enable table config persist feature
   */
  enablePersistConfig: PropTypes.bool,

  /**
   * Enable table config persist feature
   */
  enableCSVToastMessage: PropTypes.bool
};

TableProvider.defaultProps = {
  tableId: "data-grid",
  responsive: false,
  height: 400,
  uniqueDataKey: "id",
  hideHeader: false,
  fixSelectionColumn: false,
  isSelectable: false,
  multiSelect: true,
  disabledRows: [],
  isLoading: false,
  loadingMoreText: "Loading More Rows...",
  loadedAll: false,
  footerHeight: 40,
  estimatedRowHeight: 40,
  enableConfiguration: false,
  enableFullScreen: false,
  enableExportToCsv: false,
  csvFileName: "data-grid",
  enableCSVToastMessage: false,
  emptyRenderer: () => {
    return (
      <MessageBox
        isHtml
        icon={<WarningYellowOutline width={56} height={56} />}
        primaryText="Error loading data"
        secondaryText="We are facing issues while loading. <br/>Please retry again later"
      />
    );
  },
  exportPopupMaxHeight: 250,
  enableRowSpan: false,
  enablePersistConfig: false
};

export default TableProvider;
